import { Button, Icon, Label, Typography } from '@packages/ui/shared';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import type { WiringDiagramOption } from '../hooks/use-lookup-by-electrical-grid';

/**
 * Props for the {@link ElectricalGridStepOne} component.
 */
export type ElectricalGridStepOneProps = {
    /** Available wiring diagram options for the typeahead. */
    diagramOptionData: WiringDiagramOption[];

    /** Whether a diagram has been selected (enables the continue button). */
    hasDiagramSelected: boolean;

    /** Callback to navigate back to the search method selector. */
    onBack: () => void;

    /** Callback to advance to step 2 after selecting a diagram. */
    onContinue: () => void;

    /**
     * Handler for closing the modal.
     */
    onClose: () => void;

    /** Callback when the user changes the diagram typeahead selection. */
    onDiagramChange: (selected: WiringDiagramOption[]) => void;

    /** The currently selected diagram option(s). */
    selectedDiagramData: WiringDiagramOption[];
};

/**
 * Step 1 of the electrical grid lookup: select a wiring diagram subsection from a typeahead.
 *
 * @param props - {@link ElectricalGridStepOneProps}
 * @returns the step one JSX
 */

const useElectricalGridStepOneStyles = createUseStyles({
    buttonRow: {
        display: 'flex',
        gap: 15,
    },
});

/**
 * ElectricalGridStepOne component renders the first step of the electrical grid lookup flow.
 * Allows the user to select a wiring diagram subsection from a typeahead and proceed.
 *
 * @param props - ElectricalGridStepOneProps
 * @returns JSX.Element
 */
export function ElectricalGridStepOne({
    diagramOptionData,
    hasDiagramSelected,
    onBack,
    onContinue,
    onDiagramChange,
    selectedDiagramData,
    onClose,
}: ElectricalGridStepOneProps) {
    // Hooks
    const { t } = useTranslation();

    // Forwarded callback aliases
    /**
     * Handler for the back button. Navigates to the previous step.
     */
    /**
     * Handler for the back button. Navigates to the previous step.
     */
    const handleBack = onBack;
    /**
     * Handler for the continue button. Proceeds to the next step.
     */
    const handleContinue = onContinue;
    /**
     * Handler for diagram selection changes.
     *
     * @param selected - The selected wiring diagram options.
     */
    const handleDiagramChange = onDiagramChange;

    const classes = useElectricalGridStepOneStyles();
    return (
        <>
            <Modal.Header closeButton>Lookup by Electrical Grid Location</Modal.Header>
            <Modal.Body>
                <Row>
                    <Col>
                        <Label required>Select the correct wiring doc:</Label>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Form>
                            <Typeahead
                                id="fail-code-lookup-electrical-grid-modal__section"
                                onChange={handleDiagramChange}
                                options={diagramOptionData}
                                placeholder="Search"
                                selected={selectedDiagramData}
                            />
                        </Form>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
                <Button
                    iconLeft={<Icon color="blueOne" name="chevron-left" />}
                    onPress={handleBack}
                    variant="ghost-blue"
                >
                    {t('modals:failCodeSearch.actions.back', 'Back')}
                </Button>
                <div className={classes.buttonRow}>
                    <Button onPress={onClose} variant="link-blue">
                        <Typography color="blueOne">{t('qmr:sections:failCode.cancel')}</Typography>
                    </Button>
                    <Button disabled={!hasDiagramSelected} onPress={handleContinue} variant="primary">
                        Continue to Electrical Grid Location
                    </Button>
                </div>
            </Modal.Footer>
        </>
    );
}
